import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import { 
  Upload, Wand2, Palette, Home, Loader2, Download, RefreshCw,
  ChevronRight, Sparkles, Image, X, Check, Paintbrush, Grid3X3,
  Layers, Sofa, Bath, ChefHat, Bed, ArrowRight, Camera, Send,
  ImagePlus, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const ROOM_TYPES = [
  { value: 'kitchen', label: 'Kitchen', icon: ChefHat },
  { value: 'bathroom', label: 'Bathroom', icon: Bath },
  { value: 'living_room', label: 'Living Room', icon: Sofa },
  { value: 'bedroom', label: 'Bedroom', icon: Bed },
  { value: 'dining_room', label: 'Dining Room', icon: Home },
];

const QUICK_CHANGES = [
  { label: 'White Cabinets', value: 'cabinet_color:Benjamin Moore White Dove' },
  { label: 'Navy Cabinets', value: 'cabinet_color:navy blue' },
  { label: 'Sage Green Walls', value: 'wall_color:Benjamin Moore Sage Green' },
  { label: 'Marble Countertops', value: 'countertop:white marble with grey veining' },
  { label: 'Quartz Countertops', value: 'countertop:quartz with subtle grey veins' },
  { label: 'Subway Tile Backsplash', value: 'backsplash:white subway tile' },
  { label: 'Herringbone Backsplash', value: 'backsplash:herringbone marble tile' },
  { label: 'Hardwood Floors', value: 'flooring:white oak hardwood' },
  { label: 'Modern Style', value: 'style:modern minimalist' },
  { label: 'Farmhouse Style', value: 'style:modern farmhouse' },
];

const EXAMPLE_PROMPTS = [
  "Change the cabinet color to Benjamin Moore's White Dove",
  "Replace the countertops with quartz featuring grey veins",
  "Paint the walls a warm sage green and add brass hardware",
  "Make this a modern farmhouse style with shaker cabinets",
  "Add a white subway tile backsplash with dark grout",
  "Change the flooring to wide plank white oak hardwood",
  "Give this kitchen a complete modern renovation with white cabinets, marble counters, and gold fixtures",
];

const Envision = () => {
  const { token, isAuthenticated } = useAuth();
  const fileInputRef = useRef(null);
  
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageBase64, setUploadedImageBase64] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  
  // Form state
  const [roomType, setRoomType] = useState('kitchen');
  const [selectedChanges, setSelectedChanges] = useState([]);
  const [customPrompt, setCustomPrompt] = useState('');
  const [style, setStyle] = useState('modern');

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target.result);
      setUploadedImageBase64(e.target.result.split(',')[1]);
      setGeneratedImage(null);
    };
    reader.readAsDataURL(file);
    toast.success('Photo uploaded! Now describe your changes.');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const input = fileInputRef.current;
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      handleImageUpload({ target: { files: [file] } });
    }
  };

  const toggleChange = (change) => {
    setSelectedChanges(prev => 
      prev.includes(change.value)
        ? prev.filter(c => c !== change.value)
        : [...prev, change.value]
    );
  };

  const generateEnvision = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to use Envision');
      return;
    }
    
    if (!customPrompt && selectedChanges.length === 0) {
      toast.error('Please describe what changes you want to make');
      return;
    }
    
    setLoading(true);
    setLoadingText(uploadedImage ? 'Transforming your space...' : 'Creating your vision...');
    
    try {
      const response = await fetch(`${API}/api/envision/transform`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_base64: uploadedImageBase64 || null,
          room_type: roomType,
          changes: selectedChanges,
          style: style,
          custom_prompt: customPrompt || null
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Generation failed');
      }
      
      const data = await response.json();
      setGeneratedImage(`data:image/png;base64,${data.image_base64}`);
      toast.success('Your vision is ready!');
      
    } catch (error) {
      toast.error(error.message || 'Failed to generate image');
    } finally {
      setLoading(false);
      setLoadingText('');
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `envision-${roomType}-${Date.now()}.png`;
    link.click();
    toast.success('Image downloaded!');
  };

  const clearUpload = () => {
    setUploadedImage(null);
    setUploadedImageBase64(null);
    setGeneratedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetAll = () => {
    clearUpload();
    setSelectedChanges([]);
    setCustomPrompt('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white" data-testid="envision-page">
      <Header />
      
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full mb-4">
              <Sparkles className="w-4 h-4" />
              AI-Powered Room Transformation
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Envision Your Space
            </h1>
            <p className="text-neutral-500 text-lg max-w-2xl mx-auto">
              Upload a photo of your room and describe the changes you want. Our AI will transform it instantly.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Upload & Controls */}
            <div className="space-y-6">
              {/* Upload Area */}
              <div className="bg-white rounded-2xl border border-neutral-200 p-6">
                <Label className="text-lg font-semibold mb-4 block flex items-center gap-2">
                  <Camera className="w-5 h-5 text-[#FF3B30]" />
                  1. Upload Your Photo
                </Label>
                
                {!uploadedImage ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="border-2 border-dashed border-neutral-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#FF3B30] hover:bg-[#FF3B30]/5 transition-all"
                    data-testid="upload-area"
                  >
                    <div className="w-16 h-16 mx-auto bg-neutral-100 rounded-2xl flex items-center justify-center mb-4">
                      <ImagePlus className="w-8 h-8 text-neutral-400" />
                    </div>
                    <p className="font-medium text-neutral-700 mb-1">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-sm text-neutral-500">
                      JPG, PNG up to 10MB
                    </p>
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={uploadedImage} 
                      alt="Uploaded room" 
                      className="w-full rounded-xl object-cover max-h-64"
                    />
                    <button
                      onClick={clearUpload}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-2 left-2 px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Photo uploaded
                    </div>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="file-input"
                />
              </div>

              {/* Room Type & Style */}
              <div className="bg-white rounded-2xl border border-neutral-200 p-6">
                <Label className="text-lg font-semibold mb-4 block flex items-center gap-2">
                  <Home className="w-5 h-5 text-[#FF3B30]" />
                  2. Room Details
                </Label>
                
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {ROOM_TYPES.map((room) => (
                    <button
                      key={room.value}
                      onClick={() => setRoomType(room.value)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                        roomType === room.value
                          ? 'border-[#FF3B30] bg-[#FF3B30]/5 text-[#FF3B30]'
                          : 'border-neutral-200 hover:border-neutral-300 text-neutral-600'
                      }`}
                    >
                      <room.icon className="w-5 h-5" />
                      <span className="text-xs font-medium">{room.label}</span>
                    </button>
                  ))}
                </div>
                
                <div>
                  <Label className="text-sm mb-2 block">Design Style</Label>
                  <Select value={style} onValueChange={setStyle}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="modern">Modern</SelectItem>
                      <SelectItem value="farmhouse">Farmhouse</SelectItem>
                      <SelectItem value="minimalist">Minimalist</SelectItem>
                      <SelectItem value="traditional">Traditional</SelectItem>
                      <SelectItem value="scandinavian">Scandinavian</SelectItem>
                      <SelectItem value="industrial">Industrial</SelectItem>
                      <SelectItem value="coastal">Coastal</SelectItem>
                      <SelectItem value="contemporary">Contemporary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Describe Changes */}
              <div className="bg-white rounded-2xl border border-neutral-200 p-6">
                <Label className="text-lg font-semibold mb-4 block flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-[#FF3B30]" />
                  3. Describe Your Changes
                </Label>
                
                {/* Custom Prompt */}
                <div className="mb-4">
                  <Textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Example: Change the cabinet color to Benjamin Moore's White Dove, add quartz countertops with grey veins, and install a white subway tile backsplash..."
                    rows={4}
                    className="resize-none text-base"
                    data-testid="custom-prompt"
                  />
                </div>
                
                {/* Quick Change Chips */}
                <div>
                  <p className="text-sm text-neutral-500 mb-2">Or select quick changes:</p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_CHANGES.map((change) => (
                      <button
                        key={change.value}
                        onClick={() => toggleChange(change)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          selectedChanges.includes(change.value)
                            ? 'bg-[#FF3B30] text-white'
                            : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                        }`}
                      >
                        {selectedChanges.includes(change.value) && (
                          <Check className="w-3 h-3 inline mr-1" />
                        )}
                        {change.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={generateEnvision}
                disabled={loading || (!customPrompt && selectedChanges.length === 0)}
                className="w-full h-14 bg-gradient-to-r from-[#FF3B30] to-[#DC2626] hover:from-[#DC2626] hover:to-[#B91C1C] text-white rounded-xl text-lg font-medium shadow-lg"
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {loadingText || 'Generating...'}
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5 mr-2" />
                    {uploadedImage ? 'Transform My Space' : 'Generate Vision'}
                  </>
                )}
              </Button>
            </div>

            {/* Right: Preview */}
            <div className="space-y-6">
              {/* Generated Image Display */}
              <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden sticky top-28">
                <div className="p-4 border-b border-neutral-100 bg-neutral-50">
                  <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#FF3B30]" />
                    {generatedImage ? 'Your Transformed Space' : 'Preview'}
                  </h3>
                </div>
                
                <div className="aspect-[4/3] bg-neutral-100 relative">
                  {loading ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
                      <div className="relative">
                        <div className="w-24 h-24 border-4 border-purple-200 border-t-[#FF3B30] rounded-full animate-spin" />
                        <Wand2 className="w-10 h-10 text-[#FF3B30] absolute inset-0 m-auto" />
                      </div>
                      <p className="mt-6 text-neutral-700 font-medium text-lg">{loadingText || 'Creating magic...'}</p>
                      <p className="text-sm text-neutral-500 mt-2">This may take 30-60 seconds</p>
                    </div>
                  ) : generatedImage ? (
                    <img 
                      src={generatedImage} 
                      alt="Generated vision" 
                      className="w-full h-full object-cover"
                    />
                  ) : uploadedImage ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                      <img 
                        src={uploadedImage} 
                        alt="Original" 
                        className="max-h-full max-w-full object-contain rounded-lg opacity-50"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 text-center shadow-lg">
                          <Wand2 className="w-10 h-10 text-[#FF3B30] mx-auto mb-3" />
                          <p className="font-medium text-neutral-800">Ready to transform</p>
                          <p className="text-sm text-neutral-500 mt-1">Describe your changes and click Generate</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-400 p-6">
                      <div className="w-24 h-24 rounded-2xl bg-neutral-200 flex items-center justify-center mb-4">
                        <Sparkles className="w-12 h-12 text-neutral-300" />
                      </div>
                      <p className="font-medium text-neutral-500 text-center">Your transformed space will appear here</p>
                      <p className="text-sm text-neutral-400 mt-1 text-center">Upload a photo and describe your dream renovation</p>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                {generatedImage && (
                  <div className="p-4 border-t border-neutral-100 flex gap-3">
                    <Button 
                      onClick={downloadImage} 
                      className="flex-1 bg-[#FF3B30] hover:bg-[#DC2626]"
                      data-testid="download-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={generateEnvision}
                      disabled={loading}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Regenerate
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={resetAll}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Example Prompts */}
              <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-2xl p-6 text-white">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                  Example Prompts
                </h3>
                <div className="space-y-2">
                  {EXAMPLE_PROMPTS.slice(0, 5).map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => setCustomPrompt(prompt)}
                      className="block w-full text-left px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm transition-colors"
                    >
                      "{prompt}"
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-center text-neutral-900 mb-8" style={{ fontFamily: 'Outfit' }}>
              How Envision Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-16 h-16 mx-auto bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-purple-600">1</span>
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">Upload Your Photo</h3>
                <p className="text-sm text-neutral-500">
                  Take a photo of your kitchen, bathroom, or any room you want to transform
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 mx-auto bg-pink-100 rounded-2xl flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-pink-600">2</span>
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">Describe Changes</h3>
                <p className="text-sm text-neutral-500">
                  Tell us what you want — new cabinet colors, countertops, flooring, or a complete redesign
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-16 h-16 mx-auto bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                  <span className="text-2xl font-bold text-blue-600">3</span>
                </div>
                <h3 className="font-semibold text-neutral-900 mb-2">See Your Vision</h3>
                <p className="text-sm text-neutral-500">
                  Our AI transforms your space in seconds. Download and share with contractors!
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-neutral-500 mb-4">
              Love what you see? Enter our giveaways for a chance to win FREE renovations!
            </p>
            <Link to="/giveaways">
              <Button size="lg" variant="outline" className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white">
                Browse Giveaways
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Envision;
